
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import CircularArrowButton from '@/components/CircularArrowButton';
import { TempOnboardingService } from '@/services/tempOnboardingService';

type GoalOption = 'reduce_control' | 'full_abstinence' | 'maintain_sobriety' | 'identify_triggers' | 'healthy_habits' | 'rebuild_relationships' | 'monitor_progress';

export default function OnboardingGoalScreen() {
  const { logCompleted } = useOnboardingStepTracking('goal');
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Keyboard.dismiss();
    const loadSavedGoal = async () => {
      const data = await TempOnboardingService.getData();
      if (data.appGoal) {
        setSelectedGoal(data.appGoal as GoalOption);
        console.log('📥 Loaded previously saved goal:', data.appGoal);
      }
    };
    loadSavedGoal();
  }, []);

  const goalOptions = [
    { value: 'reduce_control' as GoalOption, label: t('onboarding.goal.options.reduceControl') },
    { value: 'full_abstinence' as GoalOption, label: t('onboarding.goal.options.fullAbstinence') },
    { value: 'maintain_sobriety' as GoalOption, label: t('onboarding.goal.options.maintainSobriety') },
    { value: 'identify_triggers' as GoalOption, label: t('onboarding.goal.options.identifyTriggers') },
    { value: 'healthy_habits' as GoalOption, label: t('onboarding.goal.options.healthyHabits') },
    { value: 'rebuild_relationships' as GoalOption, label: t('onboarding.goal.options.rebuildRelationships') },
    { value: 'monitor_progress' as GoalOption, label: t('onboarding.goal.options.monitorProgress') },
  ];

  const handleOptionSelect = (option: GoalOption) => {
    setSelectedGoal(option);
  };

  const handleContinue = async () => {
    if (!selectedGoal) {
      Alert.alert(
        t('onboarding.goal.required'),
        t('onboarding.goal.requiredMessage')
      );
      return;
    }

    setIsLoading(true);
    try {
      await TempOnboardingService.saveAppGoal(selectedGoal);
      console.log('✅ Goal saved:', selectedGoal);
      // PRD §5.2 — `onboarding_step_completed` for the `goal` step.
      logCompleted();
      router.push('/onboarding-notifications');
    } catch (error) {
      console.error('❌ Error saving goal:', error);
      Alert.alert(
        t('onboarding.goal.error'),
        t('onboarding.goal.errorMessage')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const progressWidth = 90; // Step 9 of 10

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <View style={styles.contentContainer}>
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.scrollViewContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>9</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('onboarding.goal.title')}</Text>
            </View>
          </View>

          <View style={styles.optionsSection}>
            {goalOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.card,
                  selectedGoal === option.value && styles.selectedCard
                ]}
                onPress={() => handleOptionSelect(option.value)}
              >
                <Text style={styles.optionText}>
                  {option.label}
                </Text>
                <View style={[
                  styles.radioButton,
                  selectedGoal === option.value && styles.selectedRadio
                ]}>
                  {selectedGoal === option.value && <View style={styles.radioInner} />}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>

        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleContinue}
            disabled={!selectedGoal || isLoading}
          />
        </View>
      </View>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: 100,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  optionsSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  card: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    width: '100%',
    minHeight: 64,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  selectedCard: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  radioButton: {
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  selectedRadio: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#FFFFFF',
  },
  radioInner: {
    backgroundColor: '#015480',
    width: 12,
    height: 12,
    borderRadius: 6,
    flexShrink: 0,
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
